import { useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import { watchAnchors, watchOpenDrills, watchUser, type AnchorRow, type DrillRow } from '../lib/session';
import { DEMO } from '../lib/devFlags';

export interface SessionState {
  user: User | null;
  loading: boolean;
  anchors: AnchorRow[];
  drills: DrillRow[];
  /** Set when a live query fails, e.g. App Check rejected the app. */
  error: string | null;
}

export function useSession(): SessionState {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [anchors, setAnchors] = useState<AnchorRow[]>([]);
  const [drills, setDrills] = useState<DrillRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (DEMO) {
      void import('../lib/demo').then(({ DEMO_UID, DEMO_EMAIL }) => {
        setUser({ uid: DEMO_UID, email: DEMO_EMAIL } as User);
        setLoading(false);
      });
      return;
    }
    return watchUser((next) => {
      setUser(next);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (DEMO) {
      let stop = () => {};
      void import('../lib/demo').then(({ demoAnchors, demoOpenDrills, subscribeDemo }) => {
        const pull = () => {
          setAnchors(demoAnchors() as unknown as AnchorRow[]);
          setDrills(demoOpenDrills() as unknown as DrillRow[]);
        };
        pull();
        stop = subscribeDemo(pull);
      });
      return () => stop();
    }
    if (!user) {
      setAnchors([]);
      setDrills([]);
      return;
    }
    setError(null);
    const stopAnchors = watchAnchors(user.uid, setAnchors);
    const stopDrills = watchOpenDrills(user.uid, setDrills);
    return () => {
      stopAnchors();
      stopDrills();
    };
  }, [user]);

  return { user, loading, anchors, drills, error };
}
